import { supabase } from '@/lib/supabase';
import { ALL_CATEGORY_PRODUCTS, type CatProduct, type SupabaseProduct } from '@/mocks/categoryProducts';

// Map category aliases to canonical category keys
export const CATEGORY_ALIAS_MAP: Record<string, string> = {
  'Trousers': 'Track Pants',
  'Track Pant': 'Track Pants',
  'track pants': 'Track Pants',
  'trousers': 'Track Pants',
  'T-Shirt': 'T-Shirts',
  't-shirts': 'T-Shirts',
  'Hoodie': 'Hoodies',
  'hoodies': 'Hoodies',
  'Short': 'Shorts',
  'shorts': 'Shorts',
  'Jean': 'Jeans',
  'jeans': 'Jeans',
};

// Get stock for a specific color+size variant from stockBreakdown
export function getVariantStock(
  stockBreakdown: Record<string, unknown> | undefined,
  colors: CatProduct['colors'],
  sizes: string[],
  colorName?: string,
  size?: string
): number {
  if (!stockBreakdown) return Infinity;
  const hasColors = (colors?.length ?? 0) > 0;
  const hasSizes = sizes.length > 0;

  if (hasColors && hasSizes) {
    if (colorName && size) {
      const colorMap = stockBreakdown[colorName] as Record<string, number> | undefined;
      return typeof colorMap?.[size] === 'number' ? colorMap[size] : 0;
    }
    if (!colorName && size) {
      let total = 0;
      (colors ?? []).forEach(cv => {
        const colorMap = stockBreakdown[cv.name] as Record<string, number> | undefined;
        total += typeof colorMap?.[size] === 'number' ? colorMap[size] : 0;
      });
      return total;
    }
    return 0;
  }
  if (hasColors && !hasSizes) {
    if (colorName) return typeof stockBreakdown[colorName] === 'number' ? (stockBreakdown[colorName] as number) : 0;
    let total = 0;
    (colors ?? []).forEach(cv => {
      total += typeof stockBreakdown[cv.name] === 'number' ? (stockBreakdown[cv.name] as number) : 0;
    });
    return total;
  }
  if (!hasColors && hasSizes) {
    if (size) return typeof stockBreakdown[size] === 'number' ? (stockBreakdown[size] as number) : 0;
    return 0;
  }
  return typeof stockBreakdown['total'] === 'number' ? (stockBreakdown['total'] as number) : 0;
}

// Get total stock across all variants
export function getTotalStock(stockBreakdown: Record<string, unknown> | undefined): number | null {
  if (!stockBreakdown) return null;
  let total = 0;
  const values = Object.values(stockBreakdown);
  for (const v of values) {
    if (typeof v === 'number') { total += v; continue; }
    if (typeof v === 'object' && v !== null) {
      for (const sv of Object.values(v as Record<string, number>)) {
        total += typeof sv === 'number' ? sv : 0;
      }
    }
  }
  return total;
}

// Module-level cache for cart image resolution
let _categoryProductsCache: CatProduct[] = [];

export function setCategoryProductsCache(products: CatProduct[]) {
  _categoryProductsCache = products;
}

export function getProductByIdFromCache(productId: number): CatProduct | undefined {
  return _categoryProductsCache.find(p => p.id === productId);
}

// Fetch products from Supabase with pagination support
async function fetchSupabaseProducts(category: string, limit = 500, offset = 0): Promise<CatProduct[]> {
  const canonical = CATEGORY_ALIAS_MAP[category] ?? category;

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('category', canonical)
    .eq('status', 'approved')
    .order('created_at', { ascending: false })
    .limit(limit)
    .range(offset, offset + limit - 1);

  if (error || !data) {
    console.error('[Products] Supabase fetch error:', error);
    return [];
  }

  const products: CatProduct[] = [];

  // Convert Supabase UUID to stable numeric ID
  // Uses last 8 hex chars of UUID → number (max ~4 billion, safe for JS)
  function uuidToNumericId(uuid: string, colorIndex = 0): number {
    const hex = uuid.replace(/-/g, '').slice(-8);
    const base = parseInt(hex, 16) % 900000000; // keep under 900M
    return base * 100 + colorIndex; // color index offset (supports up to 99 colors per product)
  }

  (data as SupabaseProduct[]).forEach((p) => {
    const stock = p.stock ?? 0;
    const allColors = p.colors?.map((c) => ({
      name: c.name, hex: c.hex, price: c.price ?? p.price, image: c.image, images: c.images,
    }));

    if (allColors && allColors.length > 0) {
      allColors.forEach((colour, cIdx) => {
        let colourStock = stock;
        let colourOutOfStock = p.stock === 0;

        if (p.stock_breakdown) {
          const bd = p.stock_breakdown;
          const hasSizes = (p.sizes?.length ?? 0) > 0;

          if (hasSizes) {
            const colourMap = bd[colour.name] as Record<string, number> | undefined;
            if (colourMap) {
              colourStock = Object.values(colourMap).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
            } else {
              colourStock = 0;
            }
          } else {
            const val = bd[colour.name];
            colourStock = typeof val === 'number' ? val : 0;
          }
          colourOutOfStock = colourStock === 0;
        }

        const colourImage = colour.image || p.thumbnail || p.images?.[0] || '';

        products.push({
          id: uuidToNumericId(p.id, cIdx),
          name: p.name,
          price: colour.price ?? p.price,
          originalPrice: p.original_price || p.price,
          category: canonical,
          badge: 'NEW',
          sizes: p.sizes && p.sizes.length > 0 ? p.sizes : ['S', 'M', 'L', 'XL'],
          colors: allColors,
          selectedColor: { name: colour.name, hex: colour.hex, price: colour.price ?? p.price },
          description: p.description,
          image: colourImage,
          images: colour.images && colour.images.length > 0 ? colour.images : p.images,
          outOfStock: colourOutOfStock,
          stock: colourStock,
          stockBreakdown: p.stock_breakdown,
        });
      });
    } else {
      products.push({
        id: uuidToNumericId(p.id, 0),
        name: p.name,
        price: p.price,
        originalPrice: p.original_price || p.price,
        category: canonical,
        badge: 'NEW',
        sizes: p.sizes && p.sizes.length > 0 ? p.sizes : ['S', 'M', 'L', 'XL'],
        colors: undefined,
        description: p.description,
        image: p.thumbnail || p.images?.[0] || '',
        images: p.images,
        outOfStock: stock === 0,
        stock,
        stockBreakdown: p.stock_breakdown,
      });
    }
  });

  return products;
}

// Load products: Supabase first, fallback to mock data if empty
// Supports pagination: page=1,2,3... and pageSize=20,50,100...
export async function getProductsForCategory(
  category: string,
  page = 1,
  pageSize = 50
): Promise<CatProduct[]> {
  const offset = (page - 1) * pageSize;
  const supabaseProducts = await fetchSupabaseProducts(category, pageSize, offset);
  if (supabaseProducts.length > 0) return supabaseProducts;
  const canonical = CATEGORY_ALIAS_MAP[category] ?? category;
  const mocks = ALL_CATEGORY_PRODUCTS[canonical] ?? [];
  // Also paginate mock data
  return mocks.slice(offset, offset + pageSize);
}

// Get total count of products in a category (for pagination)
export async function getProductCountForCategory(category: string): Promise<number> {
  const canonical = CATEGORY_ALIAS_MAP[category] ?? category;
  
  const { count, error } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .eq('category', canonical)
    .eq('status', 'approved');
  
  if (error) {
    console.error('[Products] Count fetch error:', error);
    // Fallback to mock count
    const mocks = ALL_CATEGORY_PRODUCTS[canonical] ?? [];
    return mocks.length;
  }
  
  return count || 0;
}

// Deduct variant-wise stock in Supabase after order is placed
export async function deductStockForProduct(
  product: CatProduct,
  qty = 1,
  colorName?: string,
  sizeName?: string
): Promise<void> {
  if (!product.stockBreakdown) return;

  try {
    const newBreakdown = { ...product.stockBreakdown };
    const hasColors = (product.colors?.length ?? 0) > 0;
    const hasSizes = product.sizes.length > 0;

    if (hasColors && hasSizes && colorName && sizeName) {
      const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
      const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
      newBreakdown[colorName] = { ...colorMap, [sizeName]: Math.max(0, prev - qty) };
    } else if (hasColors && !hasSizes && colorName) {
      const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
      newBreakdown[colorName] = Math.max(0, prev - qty);
    } else if (!hasColors && hasSizes && sizeName) {
      const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
      newBreakdown[sizeName] = Math.max(0, prev - qty);
    } else {
      const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (product.stock ?? 0);
      newBreakdown['total'] = Math.max(0, prev - qty);
    }

    let newTotal = 0;
    Object.values(newBreakdown).forEach(v => {
      if (typeof v === 'number') newTotal += v;
      else if (typeof v === 'object' && v !== null) {
        Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
      }
    });

    const { data: matchedProducts } = await supabase
      .from('products')
      .select('id')
      .eq('name', product.name)
      .eq('price', product.price)
      .eq('status', 'approved')
      .limit(1);

    if (matchedProducts && matchedProducts.length > 0) {
      await supabase
        .from('products')
        .update({ stock: newTotal, stock_breakdown: newBreakdown })
        .eq('id', matchedProducts[0].id);
    }
  } catch (e) {
    console.error('[Stock] Deduct failed:', e);
  }
}

// Fetch product video from Supabase
const MOCK_PRODUCT_VIDEOS: Record<number, { video: string; videoThumbnail: string }> = {
  102: {
    video: 'https://www.w3schools.com/html/mov_bbb.mp4',
    videoThumbnail: 'https://readdy.ai/api/search-image?query=Premium%20white%20essential%20t-shirt%20product%20video%20thumbnail%20frame%2C%20model%20wearing%20clean%20white%20crew%20neck%20tee%2C%20soft%20natural%20studio%20lighting%2C%20fashion%20e-commerce%20video%20still%20frame%2C%20crisp%20sharp%20detail&width=800&height=1200&seq=tshirt102vid&orientation=portrait',
  },
};

export async function fetchProductVideo(productId: number, productName: string): Promise<{ video?: string; videoThumbnail?: string }> {
  if (MOCK_PRODUCT_VIDEOS[productId]) {
    return MOCK_PRODUCT_VIDEOS[productId];
  }
  try {
    const { data, error } = await supabase
      .from('products')
      .select('video, video_thumbnail')
      .eq('name', productName)
      .limit(1)
      .maybeSingle();

    if (error || !data) return {};
    return {
      video: data.video || undefined,
      videoThumbnail: data.video_thumbnail || undefined,
    };
  } catch {
    return {};
  }
}

// Batch fetch video info for multiple product names — optimized for 100+ products
// Returns a Set of product names that have videos
export async function fetchProductsWithVideos(productNames: string[]): Promise<Set<string>> {
  if (productNames.length === 0) return new Set();
  
  try {
    // Single query to get all products with videos in this category
    const { data, error } = await supabase
      .from('products')
      .select('name, video')
      .in('name', productNames)
      .not('video', 'is', null);
    
    if (error || !data) {
      console.error('[Video] Batch fetch error:', error);
      return new Set();
    }
    
    // Return Set of product names that have videos
    return new Set(data.map(p => p.name).filter(Boolean));
  } catch (e) {
    console.error('[Video] Batch fetch failed:', e);
    return new Set();
  }
}
